const STORE_KEY = "__ARENTO_PWA_INSTALL_STORE_V3__";
const INSTALL_SLOW_AFTER_MS = 15 * 1000;
const INSTALL_CONFIRM_TIMEOUT_MS = 10 * 60 * 1000;
const INSTALL_CONFIRM_INTERVAL_MS = 700;

const isStandalone = () =>
    Boolean(
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator.standalone === true
    );

const canCheckInstalledPwa = () =>
    typeof navigator.getInstalledRelatedApps === "function";

const createStore = () => ({
    deferredPrompt: null,
    installing: false,
    installingSlow: false,
    installed: false,
    installedBy: null,
    appInstalledEventSeen: false,
    lastOutcome: null,
    promptSeen: false,
    installCheckPromise: null,
    installWatchPromise: null,
    installAttemptId: 0,
    slowTimerId: null,
    listeners: new Set(),
    handlersBound: false
});

const store = (() => {
    if (typeof window === "undefined") return createStore();

    if (!window[STORE_KEY]) {
        window[STORE_KEY] = createStore();
    }

    return window[STORE_KEY];
})();

const getState = () => ({
    isStandalone: isStandalone(),
    canPrompt: Boolean(store.deferredPrompt),
    installing: store.installing,
    installingSlow: store.installingSlow,
    installed: store.installed || isStandalone(),
    installedBy: store.installedBy,
    appInstalledEventSeen: store.appInstalledEventSeen,
    canCheckInstalledPwa: canCheckInstalledPwa(),
    lastOutcome: store.lastOutcome,
    promptSeen: store.promptSeen
});

const notify = () => {
    const state = getState();

    store.listeners.forEach(listener => {
        try {
            listener(state);
        } catch (error) {
            console.warn("PWA install listener error:", error);
        }
    });
};

const wait = ms =>
    new Promise(resolve => window.setTimeout(resolve, ms));

const clearSlowTimer = () => {
    if (!store.slowTimerId) return;
    window.clearTimeout(store.slowTimerId);
    store.slowTimerId = null;
};

const beginInstallingUi = () => {
    store.installAttemptId += 1;
    store.installing = true;
    store.installingSlow = false;
    clearSlowTimer();

    const attemptId = store.installAttemptId;

    store.slowTimerId = window.setTimeout(() => {
        if (attemptId !== store.installAttemptId || !store.installing) return;
        store.installingSlow = true;
        notify();
    }, INSTALL_SLOW_AFTER_MS);

    notify();
    return attemptId;
};

const finishInstallingUi = attemptId => {
    if (attemptId !== store.installAttemptId) return;
    clearSlowTimer();
    store.installing = false;
    store.installingSlow = false;
    notify();
};

const markInstalled = source => {
    store.installed = true;
    store.installedBy = source || store.installedBy || "unknown";
    store.deferredPrompt = null;
    store.lastOutcome = "installed";
    clearSlowTimer();
    store.installing = false;
    store.installingSlow = false;
    notify();
};

const checkInstalledPwa = async () => {
    if (isStandalone()) {
        markInstalled("standalone");
        return true;
    }

    if (!canCheckInstalledPwa()) {
        return store.installed;
    }

    if (store.installCheckPromise) {
        return store.installCheckPromise;
    }

    // Defer the check so the finally block cannot run before promise is assigned
    // when a browser throws synchronously instead of returning a Promise.
    const promise = Promise.resolve().then(async () => {
        try {
            const apps = await navigator.getInstalledRelatedApps();
            const detected = Array.isArray(apps) && apps.some(app => app?.platform === "webapp");

            if (detected) {
                markInstalled("related-apps");
            } else if (store.installed && store.installedBy === "related-apps") {
                store.installed = false;
                store.installedBy = null;
                store.lastOutcome = store.deferredPrompt ? "ready" : "unavailable";
                notify();
            }

            return detected;
        } catch (error) {
            console.warn("PWA installed-state check error:", error);
            return store.installed;
        } finally {
            if (store.installCheckPromise === promise) {
                store.installCheckPromise = null;
            }
        }
    });

    store.installCheckPromise = promise;
    return promise;
};

const waitForInstalledPwa = async ({
    timeoutMs = INSTALL_CONFIRM_TIMEOUT_MS,
    intervalMs = INSTALL_CONFIRM_INTERVAL_MS
} = {}) => {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
        if (store.installed || isStandalone()) return true;
        if (await checkInstalledPwa()) return true;
        await wait(intervalMs);
    }

    return store.installed || isStandalone();
};

const ensureInstallWatch = () => {
    if (store.installWatchPromise) {
        return store.installWatchPromise;
    }

    const promise = waitForInstalledPwa()
        .finally(() => {
            if (store.installWatchPromise === promise) {
                store.installWatchPromise = null;
            }
        });

    store.installWatchPromise = promise;
    return promise;
};

const handleBeforeInstallPrompt = event => {
    event.preventDefault();
    store.deferredPrompt = event;
    store.promptSeen = true;
    store.lastOutcome = "ready";
    store.installed = false;
    store.installedBy = null;
    store.appInstalledEventSeen = false;
    notify();
};

const handleAppInstalled = () => {
    store.appInstalledEventSeen = true;

    /*
     * В Chrome Android appinstalled может прийти раньше, чем WebAPK
     * уже виден через системную проверку installed-related-apps.
     * Поэтому при наличии getInstalledRelatedApps() это только сигнал
     * "браузер закончил свою часть", но НЕ финальное подтверждение.
     */
    if (canCheckInstalledPwa()) {
        store.lastOutcome = "appinstalled-waiting";
        notify();
        ensureInstallWatch();
        return;
    }

    /*
     * В браузерах без getInstalledRelatedApps() более сильного сигнала
     * у веб-страницы нет. Там appinstalled остаётся лучшим доступным
     * подтверждением установки.
     */
    markInstalled("appinstalled");
};

const handlePossibleInstallStateChange = () => {
    if (document.visibilityState && document.visibilityState !== "visible") return;
    checkInstalledPwa();
};

if (typeof window !== "undefined" && !store.handlersBound) {
    store.handlersBound = true;

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("focus", handlePossibleInstallStateChange);
    document.addEventListener("visibilitychange", handlePossibleInstallStateChange);

    checkInstalledPwa();
}

export const getPwaInstallState = () => getState();

export const subscribePwaInstallState = listener => {
    store.listeners.add(listener);
    listener(getState());
    checkInstalledPwa();

    return () => {
        store.listeners.delete(listener);
    };
};

export const refreshPwaInstallState = () => checkInstalledPwa();

export const promptPwaInstall = async () => {
    if (isStandalone()) {
        markInstalled("standalone");

        return {
            ok: true,
            installed: true,
            outcome: "already-installed"
        };
    }

    if (store.installing) {
        return { ok: true, installed: store.installed, outcome: "installing", pending: true };
    }

    const promptEvent = store.deferredPrompt;

    if (!promptEvent) {
        if (await checkInstalledPwa()) {
            return { ok: true, installed: true, outcome: "already-installed" };
        }
        store.lastOutcome = "unavailable";
        notify();

        return {
            ok: false,
            installed: false,
            outcome: "unavailable",
            reason: "install-prompt-unavailable"
        };
    }

    store.deferredPrompt = null;
    store.lastOutcome = "prompt-open";
    const attemptId = beginInstallingUi();

    let choice = null;
    try {
        // Call prompt synchronously from the tap, before any await. Observe both
        // Promises immediately so either rejection releases the loading state.
        const prompted = promptEvent.prompt();
        const outcomes = await Promise.all([prompted, promptEvent.userChoice]);
        choice = outcomes[1] || outcomes[0];
    } catch (error) {
        store.lastOutcome = "unavailable";
        finishInstallingUi(attemptId);
        throw error;
    }

    if (choice?.outcome === "dismissed") {
        store.lastOutcome = "dismissed";
        finishInstallingUi(attemptId);

        return {
            ok: true,
            installed: false,
            outcome: "dismissed"
        };
    }

    if (choice?.outcome !== "accepted") {
        store.lastOutcome = choice?.outcome || "unknown";
        finishInstallingUi(attemptId);

        return {
            ok: true,
            installed: false,
            outcome: choice?.outcome || "unknown"
        };
    }

    store.lastOutcome = "accepted-waiting";
    notify();

    const confirmed = await ensureInstallWatch();

    if (confirmed || store.installed) {
        return {
            ok: true,
            installed: true,
            outcome: "accepted",
            pending: false
        };
    }

    store.lastOutcome = "accepted-pending";
    finishInstallingUi(attemptId);

    return {
        ok: true,
        installed: false,
        outcome: "accepted",
        pending: true
    };
};
